import { DealerAddress } from '@api/types';
import { TextCodes } from '@api/types/translation/TextCodes';

import {
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  TextField,
} from '@mui/material';
import { t } from '@translations';
import { Control, Controller, FieldErrors } from 'react-hook-form';

import { useGetDealerDetailsQuery } from '@api/customer.api';
import { CheckoutForm } from '@components/blocks/checkout/CheckoutBlock/CheckoutBlock';
import { useAuth } from '@hooks';
type DeliveryInformationProps = {
  control: Control<CheckoutForm>;
  errors: FieldErrors<CheckoutForm>;
  address?: DealerAddress[];
};

export const DeliveryInformation = ({ control, errors, address }: DeliveryInformationProps) => {
  const { customerNo } = useAuth();
  const { isLoading, isFetching } = useGetDealerDetailsQuery(customerNo || '');
  const isWorking = isLoading || isFetching;

  const createAddressString = (data?: DealerAddress): string => {
    const keys: Array<keyof DealerAddress> = ['name', 'line1', 'line2', 'postalCode', 'city'];
    if (data) {
      const address = keys
        .map((key) => data[key])
        .filter(Boolean)
        .join(', ');

      return address || '';
    }
    return '';
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        {isWorking ? (
          <Skeleton variant='rectangular' height={44} />
        ) : address?.length ? (
          <Controller
            name='deliveryAddressId'
            control={control}
            rules={{ required: true }}
            defaultValue={address[0].addressId || ''}
            render={({ field }) => (
              <FormControl fullWidth sx={{ mt: 4 }} error={!!errors.deliveryAddressId}>
                <InputLabel id='delivery-address'>
                  {t(TextCodes.Product.Checkout.DeliveryAddress)}
                </InputLabel>
                <Select
                  labelId='delivery-address'
                  id='select-delivery'
                  {...field}
                  onChange={(event) => {
                    field.onChange(event.target.value);
                  }}
                >
                  {address.map((option: DealerAddress, index: number) => (
                    <MenuItem value={option.addressId} key={option.addressId}>
                      {createAddressString(option)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          />
        ) : null}
      </Grid>
      <Grid item xs={12}>
        <Controller
          name='deliveryNote'
          control={control}
          defaultValue=''
          rules={{ maxLength: 40 }}
          render={({ field, formState: { errors } }) => (
            <TextField
              sx={{ mt: 2 }}
              label={t(TextCodes.Product.Checkout.DeliveryNote)}
              fullWidth
              {...field}
              error={!!errors.deliveryNote}
              helperText={errors.deliveryNote && t(TextCodes.Product.Checkout.CharacterLimitError)}
            />
          )}
        />
      </Grid>
      <Grid item xs={12}>
        <Controller
          name='orderReference'
          control={control}
          defaultValue=''
          rules={{ maxLength: 40 }}
          render={({ field, formState: { errors } }) => (
            <TextField
              sx={{ mt: 2 }}
              label={t(TextCodes.Product.Checkout.OrderReference)}
              fullWidth
              {...field}
              error={!!errors.orderReference}
              helperText={
                errors.orderReference && t(TextCodes.Product.Checkout.CharacterLimitError)
              }
            />
          )}
        />
      </Grid>
    </Grid>
  );
};
